<template>
    <div class="wc">

        <!-- Stats row -->
        <div class="wc-stats">
            <div class="wc-stat-card">
                <div class="wc-stat-icon wc-stat-icon--primary" style="background:rgba(16,185,129,0.12);border-color:rgba(16,185,129,0.22);color:#10b981;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <p class="wc-stat-lbl">{{ t('availableBalance') }}</p>
                <p class="wc-stat-val" style="color:#10b981;">{{ currencySymbol }}{{ formatNumber(availableBalance) }}</p>
                <span class="wc-stat-sub" style="color:#10b981;">E-wallet · {{ t('canWithdraw') }}</span>
            </div>
            <div class="wc-stat-card">
                <div class="wc-stat-icon" style="background:rgba(96,165,250,0.12);border-color:rgba(96,165,250,0.22);color:#60a5fa;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                </div>
                <p class="wc-stat-lbl">{{ t('totalWithdrawals') }}</p>
                <p class="wc-stat-val">{{ withdrawals?.total || 0 }}</p>
                <span class="wc-stat-sub" :style="`color:${themeColors.primary};`">{{ t('allTime') }}</span>
            </div>
            <div class="wc-stat-card">
                <div class="wc-stat-icon" style="background:rgba(74,222,128,0.12);border-color:rgba(74,222,128,0.22);color:#4ade80;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <p class="wc-stat-lbl">{{ t('successfulWithdrawals') }}</p>
                <p class="wc-stat-val">{{ successfulWithdrawals }}</p>
                <span class="wc-stat-sub" style="color:#4ade80;">{{ t('completed') }}</span>
            </div>
            <div class="wc-stat-card">
                <div class="wc-stat-icon" style="background:rgba(251,191,36,0.12);border-color:rgba(251,191,36,0.22);color:#fbbf24;">
                    <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <p class="wc-stat-lbl">{{ t('pendingWithdrawals') }}</p>
                <p class="wc-stat-val">{{ pendingWithdrawals }}</p>
                <span class="wc-stat-sub" style="color:#fbbf24;">{{ t('processing') }}</span>
            </div>
        </div>

        <!-- Form section -->
        <div class="wc-main">
            <!-- Left col: gateway + form -->
            <div class="wc-left">
                <div class="wc-card">
                    <div class="wc-card-head">
                        <div>
                            <h3 class="wc-card-title">{{ t('selectWithdrawalMethod') }}</h3>
                            <p class="wc-card-sub">{{ t('chooseGateway') }}</p>
                        </div>
                    </div>

                    <!-- Gateway grid -->
                    <div class="wc-gateways">
                        <div
                            v-for="gateway in withdrawalGateways"
                            :key="gateway.id"
                            class="wc-gateway"
                            :class="selectedGateway?.id === gateway.id ? 'wc-gateway--on' : ''"
                            :style="selectedGateway?.id === gateway.id
                                ? `border-color:rgba(${themeColors.primaryRgb},0.5);background:rgba(${themeColors.primaryRgb},0.06);`
                                : ''"
                            @click="selectGateway(gateway)"
                        >
                            <div class="wc-gateway-icon" :style="`background:linear-gradient(135deg,#7c3aed,${themeColors.primary});`">
                                <span class="wc-gateway-abbr">{{ gateway.name.substring(0, 2).toUpperCase() }}</span>
                            </div>
                            <div class="wc-gateway-info">
                                <p class="wc-gateway-name">{{ gateway.name }}</p>
                                <span class="wc-gateway-fee" :style="`color:${themeColors.primary};background:rgba(${themeColors.primaryRgb},0.12);`">
                                    {{ gateway.fixed_charge > 0 ? currencySymbol + formatNumber(gateway.fixed_charge) + ' + ' : '' }}{{ formatNumber(gateway.percent_charge) }}% {{ t('fee') }}
                                </span>
                            </div>
                            <div v-if="selectedGateway?.id === gateway.id" class="wc-gateway-check" :style="`background:rgba(${themeColors.primaryRgb},0.18);color:${themeColors.primary};`">
                                <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Instructions -->
                    <div v-if="selectedGateway && selectedGateway.description" class="wc-instructions" :style="`border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.05);`">
                        <p class="wc-instructions-title" :style="`color:${themeColors.primary};`">{{ t('withdrawalInstructions') }}</p>
                        <div class="wc-instructions-body">{{ selectedGateway.description }}</div>
                    </div>

                    <!-- BSC USDT withdrawal: dedicated component -->
                    <div v-if="selectedGateway && selectedGateway.slug === 'bsc-usdt'" class="wc-form-wrap">
                        <BscUsdtWithdrawal
                            :gateway="selectedGateway"
                            :wallet-balance="ewalletBalance"
                            :saved-wallets="bscWallets"
                            :withdrawals="withdrawals"
                        />
                    </div>

                    <!-- Standard withdrawal form -->
                    <div v-else-if="selectedGateway" class="wc-form-wrap">
                        <form class="wc-form" @submit.prevent="submitWithdrawal">
                            <div class="wc-field">
                                <label class="wc-field-lbl">{{ t('withdrawalAmount') }} ({{ selectedGateway.currency }})</label>
                                <input
                                    v-model.number="withdrawalForm.amount"
                                    type="number"
                                    step="0.01"
                                    :min="selectedGateway.min_amount"
                                    :max="selectedGateway.max_amount"
                                    class="wc-input"
                                    :placeholder="t('enterAmount')"
                                    required
                                />
                                <p class="wc-field-hint">{{ t('min') }}: {{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(selectedGateway.min_amount) }} — {{ t('max') }}: {{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(selectedGateway.max_amount) }}</p>
                            </div>

                            <div v-if="selectedGateway.parameters && selectedGateway.parameters.length > 0" class="wc-params">
                                <p class="wc-params-title">{{ t('withdrawalInformation') }}</p>
                                <div v-for="param in selectedGateway.parameters" :key="param.field_name" class="wc-field">
                                    <label class="wc-field-lbl">
                                        {{ param.field_label }}
                                        <span v-if="param.field_required" class="wc-required">*</span>
                                    </label>
                                    <input
                                        v-if="['text', 'email', 'tel', 'number', 'date', 'datetime-local'].includes(param.field_type)"
                                        v-model="withdrawalDetails[param.field_name]"
                                        :type="param.field_type"
                                        :placeholder="param.field_placeholder"
                                        :required="param.field_required"
                                        class="wc-input"
                                    />
                                    <select
                                        v-else-if="param.field_type === 'select'"
                                        v-model="withdrawalDetails[param.field_name]"
                                        :required="param.field_required"
                                        class="wc-input wc-select"
                                    >
                                        <option value="">{{ param.field_placeholder || t('selectOption') }}</option>
                                        <option v-for="(label, value) in param.field_options" :key="value" :value="value">{{ label }}</option>
                                    </select>
                                    <input
                                        v-else-if="param.field_type === 'file'"
                                        type="file"
                                        :required="param.field_required"
                                        accept="image/*,.pdf"
                                        class="wc-file-input"
                                        @change="handleFileUpload($event, param.field_name)"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                :disabled="!canSubmit || isProcessing"
                                class="wc-submit"
                                :style="canSubmit
                                    ? `background:linear-gradient(135deg,#7c3aed,${themeColors.primary});box-shadow:0 4px 18px rgba(${themeColors.primaryRgb},0.35);color:#0f172a;`
                                    : 'background:rgba(255,255,255,0.05);color:rgba(148,163,184,0.4);'"
                            >
                                <svg v-if="isProcessing" class="wc-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                </svg>
                                {{ isProcessing ? t('processing') + '...' : t('submitWithdrawalRequest') }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <!-- Right col: summary + gateway info + tips -->
            <div class="wc-right">
                <!-- Summary -->
                <div v-if="selectedGateway && isValidAmount" class="wc-card wc-summary">
                    <h3 class="wc-card-title">{{ t('withdrawalSummary') }}</h3>
                    <div class="wc-summary-rows">
                        <div class="wc-sum-row">
                            <span class="wc-sum-lbl">{{ t('requestedAmount') }}</span>
                            <span class="wc-sum-val">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(withdrawalForm.amount) }}</span>
                        </div>
                        <div v-if="calculatedCharge > 0" class="wc-sum-row">
                            <span class="wc-sum-lbl">{{ t('processingFee') }}</span>
                            <span class="wc-sum-val" style="color:#fbbf24;">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(calculatedCharge) }}</span>
                        </div>
                        <div class="wc-sum-row wc-sum-total">
                            <span class="wc-sum-lbl--b">{{ t('youWillReceive') }}</span>
                            <span class="wc-sum-total-val" :style="`color:${themeColors.primary};`">{{ getGatewayCurrencySymbol(selectedGateway) }}{{ formatNumber(finalAmount) }}</span>
                        </div>
                    </div>
                    <div class="wc-deduct" style="background:rgba(239,68,68,0.07);border-color:rgba(239,68,68,0.22);">
                        <span class="wc-deduct-lbl">Deducted from E-wallet</span>
                        <span class="wc-deduct-val" style="color:#f87171;">{{ currencySymbol }}{{ formatNumber(withdrawalForm.amount) }}</span>
                    </div>
                </div>

                <!-- Gateway image + name -->
                <div v-if="selectedGateway" class="wc-card">
                    <div v-if="selectedGateway.file" class="wc-gateway-logo">
                        <img :src="`/assets/files/${selectedGateway.file}`" :alt="selectedGateway.name" class="wc-gateway-logo-img" @error="$event.target.style.display='none'"/>
                    </div>
                    <h3 class="wc-card-title wc-center">{{ selectedGateway.name }}</h3>
                    <div class="wc-details-list">
                        <div class="wc-detail-row"><span class="wc-detail-lbl">{{ t('currency') }}</span><span class="wc-detail-val">{{ selectedGateway.currency }}</span></div>
                        <div class="wc-detail-row"><span class="wc-detail-lbl">{{ t('type') }}</span><span class="wc-detail-val" style="text-transform:capitalize;">{{ selectedGateway.type }}</span></div>
                        <div class="wc-detail-row"><span class="wc-detail-lbl">{{ t('processing') }}</span><span class="wc-detail-val" :style="`color:${themeColors.primary};`">{{ t('instantManual') }}</span></div>
                    </div>
                </div>

                <!-- Tips -->
                <div class="wc-card">
                    <h3 class="wc-card-title">{{ t('quickTips') }}</h3>
                    <div class="wc-tips">
                        <div class="wc-tip">
                            <div class="wc-tip-dot" :style="`background:${themeColors.primary};`"></div>
                            <div>
                                <p class="wc-tip-title">{{ t('verifyAccountInfo') }}</p>
                                <p class="wc-tip-sub">{{ t('doubleCheckWithdrawalDetails') }}</p>
                            </div>
                        </div>
                        <div class="wc-tip">
                            <div class="wc-tip-dot" style="background:#fbbf24;"></div>
                            <div>
                                <p class="wc-tip-title">Processing Time</p>
                                <p class="wc-tip-sub">Withdrawals are typically processed within 24 hours.</p>
                            </div>
                        </div>
                        <div class="wc-tip">
                            <div class="wc-tip-dot" style="background:#60a5fa;"></div>
                            <div>
                                <p class="wc-tip-title">Minimum Amount</p>
                                <p class="wc-tip-sub">Check each gateway's minimum withdrawal limit.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- History section -->
        <div class="wc-hist-card">
            <div class="wc-hist-head">
                <div class="wc-hist-head-icon" :style="`background:rgba(${themeColors.primaryRgb},0.12);border-color:rgba(${themeColors.primaryRgb},0.22);color:${themeColors.primary};`">
                    <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                </div>
                <div>
                    <h3 class="wc-hist-title">{{ t('withdrawalHistory') }}</h3>
                    <p class="wc-hist-sub">{{ t('trackYourWithdrawals') }}</p>
                </div>
                <div class="wc-hist-chip" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.2);`">
                    <span :style="`color:${themeColors.primary};`">{{ withdrawals?.total || 0 }}</span>
                    <span>{{ t('totalTransactions') }}</span>
                </div>
            </div>

            <!-- Empty -->
            <div v-if="!withdrawals?.data || withdrawals.data.length === 0" class="wc-empty">
                <div class="wc-empty-ic" :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.18);color:rgba(${themeColors.primaryRgb},0.6);`">
                    <svg width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                </div>
                <p class="wc-empty-title">{{ t('noWithdrawalsYet') }}</p>
                <p class="wc-empty-sub">{{ t('makeYourFirstWithdrawal') }}</p>
            </div>

            <!-- Mobile rows -->
            <div v-else class="wc-mob">
                <div v-for="w in withdrawals?.data" :key="w.id" class="wc-row">
                    <div class="wc-row-l">
                        <span class="wc-row-dot" :style="`background:${getStatusBadgeColor(w.status)};box-shadow:0 0 7px ${getStatusBadgeColor(w.status)}55;`"></span>
                        <div>
                            <p class="wc-row-trx">{{ w.trx }}</p>
                            <p class="wc-row-date">{{ formatDate(w.created_at) }}</p>
                        </div>
                    </div>
                    <div class="wc-row-r">
                        <p class="wc-row-amount" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(w.final_amount) }}</p>
                        <span class="wc-row-badge" :style="`background:${getStatusBadgeColor(w.status)}1a;border-color:${getStatusBadgeColor(w.status)}35;color:${getStatusBadgeColor(w.status)};`">{{ formatStatus(w.status) }}</span>
                    </div>
                    <div class="wc-row-actions">
                        <button v-if="w.status === 'pending'" class="wc-cancel-btn" @click="showCancelConfirmation(w)">{{ t('cancel') }}</button>
                        <button class="wc-view-btn" :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`" @click="showWithdrawalDetails(w)">{{ t('viewDetails') }}</button>
                    </div>
                </div>
            </div>

            <!-- Desktop table -->
            <div v-if="withdrawals?.data && withdrawals.data.length > 0" class="wc-tbl-wrap">
                <table class="wc-tbl">
                    <thead>
                        <tr class="wc-tbl-hdr">
                            <th class="wc-th">{{ t('trxId') }}</th>
                            <th class="wc-th">{{ t('requested') }}</th>
                            <th class="wc-th">{{ t('youReceive') }}</th>
                            <th class="wc-th">{{ t('gateway') }}</th>
                            <th class="wc-th">{{ t('status') }}</th>
                            <th class="wc-th">{{ t('date') }}</th>
                            <th class="wc-th">{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="w in withdrawals?.data" :key="w.id" class="wc-tr">
                            <td class="wc-td"><span class="wc-mono">{{ w.trx }}</span></td>
                            <td class="wc-td">
                                <span class="wc-td-val">{{ currencySymbol }}{{ formatNumber(w.amount) }}</span>
                                <span v-if="w.charge > 0" class="wc-td-fee">-{{ currencySymbol }}{{ formatNumber(w.charge) }} {{ t('fee') }}</span>
                            </td>
                            <td class="wc-td">
                                <span class="wc-td-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(w.final_amount) }}</span>
                            </td>
                            <td class="wc-td"><span class="wc-td-muted">{{ w.withdrawal_gateway?.name || t('notAvailable') }}</span></td>
                            <td class="wc-td">
                                <span class="wc-badge" :style="`background:${getStatusBadgeColor(w.status)}1a;border-color:${getStatusBadgeColor(w.status)}35;color:${getStatusBadgeColor(w.status)};`">
                                    <i class="wc-badge-dot" :style="`background:${getStatusBadgeColor(w.status)};`"></i>
                                    {{ formatStatus(w.status) }}
                                </span>
                            </td>
                            <td class="wc-td">
                                <p class="wc-td-date">{{ formatDate(w.created_at) }}</p>
                                <p class="wc-td-time">{{ formatTime(w.created_at) }}</p>
                            </td>
                            <td class="wc-td">
                                <div style="display:flex;align-items:center;gap:8px;">
                                    <button v-if="w.status === 'pending'" class="wc-tbl-cancel" @click="showCancelConfirmation(w)">{{ t('cancel') }}</button>
                                    <button class="wc-tbl-view" :style="`color:${themeColors.primary};border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.08);`" @click="showWithdrawalDetails(w)">
                                        <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                                        </svg>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="withdrawals?.data && withdrawals.data.length > 0 && withdrawals.links" class="wc-pagi">
                <span class="wc-pagi-info">{{ t('showingResults', { from: withdrawals.from||0, to: withdrawals.to||0, total: withdrawals.total||0 }) }}</span>
                <div class="wc-pagi-btns">
                    <button v-for="(link, i) in withdrawals.links" :key="i" :disabled="!link.url"
                        class="wc-pagi-btn"
                        :class="link.active ? 'wc-pagi-btn--on' : !link.url ? 'wc-pagi-btn--off' : ''"
                        :style="link.active ? `background:${themeColors.primary};color:#0f172a;box-shadow:0 4px 12px rgba(${themeColors.primaryRgb},0.3);` : ''"
                        @click="goToPage(link.url)" v-html="link.label"
                    />
                </div>
            </div>
        </div>
    </div>

    <!-- Detail Modal -->
    <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="showModal" class="wc-overlay" @click.self="closeModal">
            <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
                <div v-if="showModal" class="wc-modal" role="dialog" aria-modal="true">
                    <div class="wc-modal-head">
                        <h3 class="wc-modal-title">{{ t('withdrawalDetails') }}</h3>
                        <button class="wc-modal-close" @click="closeModal">
                            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>

                    <div v-if="selectedWithdrawal" class="wc-modal-body">
                        <!-- Header card -->
                        <div class="wc-modal-hcard">
                            <div class="wc-modal-hcard-l">
                                <div class="wc-modal-hcard-icon" :style="`background:rgba(${themeColors.primaryRgb},0.15);color:${themeColors.primary};`">
                                    <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                                    </svg>
                                </div>
                                <div>
                                    <p class="wc-modal-plan">{{ selectedWithdrawal.withdrawal_gateway?.name || t('withdrawal') }}</p>
                                    <p class="wc-modal-plan-sub">{{ t('withdrawalRequest') || 'Withdrawal Request' }}</p>
                                </div>
                            </div>
                            <span class="wc-badge" :style="`background:${getStatusBadgeColor(selectedWithdrawal.status)}1a;border-color:${getStatusBadgeColor(selectedWithdrawal.status)}35;color:${getStatusBadgeColor(selectedWithdrawal.status)};`">
                                <i class="wc-badge-dot wc-badge-dot--pulse" :style="`background:${getStatusBadgeColor(selectedWithdrawal.status)};`"></i>
                                {{ formatStatus(selectedWithdrawal.status) }}
                            </span>
                        </div>
                        <div class="wc-modal-trx" :style="`color:${themeColors.primary};`">TXN: {{ selectedWithdrawal.trx }}</div>

                        <!-- Info grid -->
                        <div class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('transactionInformation') }}</p>
                            <div class="wc-modal-grid2">
                                <div class="wc-modal-field"><span class="wc-modal-field-lbl">{{ t('transactionId') }}</span><span class="wc-modal-field-val wc-mono" :style="`color:${themeColors.primary};`">{{ selectedWithdrawal.trx }}</span></div>
                                <div class="wc-modal-field"><span class="wc-modal-field-lbl">{{ t('status') }}</span>
                                    <span class="wc-badge" :style="`background:${getStatusBadgeColor(selectedWithdrawal.status)}1a;border-color:${getStatusBadgeColor(selectedWithdrawal.status)}35;color:${getStatusBadgeColor(selectedWithdrawal.status)};`">{{ formatStatus(selectedWithdrawal.status) }}</span>
                                </div>
                                <div class="wc-modal-field"><span class="wc-modal-field-lbl">{{ t('withdrawalGateway') }}</span><span class="wc-modal-field-val">{{ selectedWithdrawal.withdrawal_gateway?.name || t('notAvailable') }}</span></div>
                                <div class="wc-modal-field"><span class="wc-modal-field-lbl">{{ t('currency') }}</span><span class="wc-modal-field-val">{{ selectedWithdrawal.currency || 'USD' }}</span></div>
                            </div>
                        </div>

                        <!-- Amount details -->
                        <div class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('amountDetails') }}</p>
                            <div class="wc-modal-amounts">
                                <div class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('requestedAmount') }}</span><span class="wc-modal-amount-val">{{ currencySymbol }}{{ formatNumber(selectedWithdrawal.amount) }}</span></div>
                                <div v-if="selectedWithdrawal.charge > 0" class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('processingFee') }}</span><span class="wc-modal-amount-val" style="color:#fbbf24;">{{ currencySymbol }}{{ formatNumber(selectedWithdrawal.charge) }}</span></div>
                                <div class="wc-modal-amount-row wc-modal-amount-total">
                                    <span class="wc-modal-amount-lbl--b">{{ t('youReceived') }}</span>
                                    <span class="wc-modal-total-val" :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(selectedWithdrawal.final_amount) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Blockchain TX hash (BSC USDT on-chain) -->
                        <div v-if="selectedWithdrawal.blockchain_tx_hash" class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">Blockchain Transaction</p>
                            <div class="wc-modal-amounts">
                                <div class="wc-modal-amount-row">
                                    <span class="wc-modal-amount-lbl">TX Hash</span>
                                    <a
                                        :href="`https://bscscan.com/tx/${selectedWithdrawal.blockchain_tx_hash}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="wc-mono"
                                        :style="`color:${themeColors.primary};word-break:break-all;font-size:0.78rem;`"
                                    >{{ selectedWithdrawal.blockchain_tx_hash }}</a>
                                </div>
                            </div>
                        </div>

                        <!-- User data -->
                        <div v-if="selectedWithdrawal.user_data && Object.keys(selectedWithdrawal.user_data).filter(k => !k.startsWith('_')).length > 0" class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('withdrawalDetails') }}</p>
                            <div class="wc-modal-amounts">
                                <div v-for="(value, key) in selectedWithdrawal.user_data" :key="key" class="wc-modal-amount-row" v-show="!key.startsWith('_')">
                                    <span class="wc-modal-amount-lbl" style="text-transform:capitalize;">{{ formatFieldName(key) }}</span>
                                    <span class="wc-modal-amount-val">{{ value || t('notAvailable') }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Timeline -->
                        <div class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('timeline') }}</p>
                            <div class="wc-modal-amounts">
                                <div class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('created') }}</span><span class="wc-modal-amount-val">{{ formatDateTime(selectedWithdrawal.created_at) }}</span></div>
                                <div v-if="selectedWithdrawal.approved_at" class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('approved') }}</span><span class="wc-modal-amount-val" :style="`color:${themeColors.primary};`">{{ formatDateTime(selectedWithdrawal.approved_at) }}</span></div>
                                <div v-if="selectedWithdrawal.rejected_at" class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('rejected') }}</span><span class="wc-modal-amount-val" style="color:#f87171;">{{ formatDateTime(selectedWithdrawal.rejected_at) }}</span></div>
                            </div>
                        </div>

                        <!-- Admin response -->
                        <div v-if="selectedWithdrawal.admin_response" class="wc-modal-section">
                            <p class="wc-modal-section-title" :style="`color:${themeColors.primary};`">{{ t('adminResponse') }}</p>
                            <p class="wc-modal-note">{{ selectedWithdrawal.admin_response }}</p>
                        </div>
                    </div>

                    <div class="wc-modal-foot">
                        <button class="wc-close-btn" @click="closeModal">{{ t('close') }}</button>
                    </div>
                </div>
            </Transition>
        </div>
    </Transition>

    <!-- Cancel Modal -->
    <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="showCancelModal" class="wc-overlay" @click.self="closeCancelModal">
            <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
                <div v-if="showCancelModal" class="wc-modal wc-modal--sm" role="dialog" aria-modal="true">
                    <div class="wc-modal-head">
                        <h3 class="wc-modal-title">{{ t('cancelWithdrawal') }}</h3>
                        <button class="wc-modal-close" @click="closeCancelModal">
                            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <div v-if="withdrawalToCancel" class="wc-modal-body">
                        <div class="wc-cancel-icon-wrap">
                            <div class="wc-cancel-icon">
                                <svg width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
                            </div>
                            <h4 class="wc-cancel-heading">{{ t('cancelWithdrawalRequest') }}</h4>
                            <p class="wc-cancel-desc">{{ t('cancelWithdrawalConfirmation') }}</p>
                        </div>
                        <div class="wc-cancel-info">
                            <div class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('transactionId') }}</span><span class="wc-mono wc-modal-amount-val">{{ withdrawalToCancel.trx }}</span></div>
                            <div class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('amount') }}</span><span class="wc-modal-amount-val">{{ currencySymbol }}{{ formatNumber(withdrawalToCancel.amount) }}</span></div>
                            <div class="wc-modal-amount-row"><span class="wc-modal-amount-lbl">{{ t('gateway') }}</span><span class="wc-modal-amount-val">{{ withdrawalToCancel.withdrawal_gateway?.name }}</span></div>
                        </div>
                        <div class="wc-refund-notice" :style="`border-color:rgba(${themeColors.primaryRgb},0.25);background:rgba(${themeColors.primaryRgb},0.06);`">
                            <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${themeColors.primary};`"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                            <p class="wc-refund-text"><strong :style="`color:${themeColors.primary};`">{{ currencySymbol }}{{ formatNumber(withdrawalToCancel.amount) }}</strong> {{ t('willBeRefunded') }}</p>
                        </div>
                    </div>
                    <div class="wc-modal-foot" style="justify-content:flex-end;gap:10px;">
                        <button class="wc-close-btn" @click="closeCancelModal">{{ t('keepWithdrawal') }}</button>
                        <button :disabled="isCancelling" class="wc-cancel-confirm-btn" @click="confirmCancelWithdrawal">
                            <svg v-if="isCancelling" class="wc-spin" width="16" height="16" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                            {{ isCancelling ? t('cancelling') : t('yesCancelWithdrawal') }}
                        </button>
                    </div>
                </div>
            </Transition>
        </div>
    </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useToast } from "@/composables/useToast.js";
import { useTranslation } from '@/composables/useTranslation';
import BscUsdtWithdrawal from '@/Components/User/Finance/BscUsdtWithdrawal.vue';

const props = defineProps({
    withdrawals: {
        type: Object,
        required: true,
        default: () => ({ data: [], total: 0, links: [] })
    },
    withdrawalGateways: {
        type: Array,
        required: true,
        default: () => []
    },
    walletBalance: {
        type: [Number, String],
        default: 0
    },
    ewalletBalance: {
        type: [Number, String],
        default: 0
    },
    bscWallets: {
        type: Array,
        default: () => []
    },
});

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

const selectedGateway = ref(null);
const isProcessing = ref(false);
const withdrawalDetails = ref({});
const showModal = ref(false);
const selectedWithdrawal = ref(null);
const showCancelModal = ref(false);
const withdrawalToCancel = ref(null);
const isCancelling = ref(false);
const balanceSource = ref('ewallet');

const withdrawalForm = ref({
    amount: null,
    withdrawal_gateway_id: null
});

const { showToast } = useToast();
const { t } = useTranslation();
const page = usePage();

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 134, g: 239, b: 172 };
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const currencySymbol = computed(() => page.props.currencySymbol || '$');

const availableBalance = computed(() => {
    const raw = parseFloat(props.ewalletBalance);
    return isNaN(raw) ? 0 : Math.max(0, raw);
});

const isValidAmount = computed(() => {
    if (!selectedGateway.value) return false;
    const amount = parseFloat(withdrawalForm.value.amount);
    if (isNaN(amount) || amount <= 0) return false;
    const minAmount = parseFloat(selectedGateway.value.min_amount || 0);
    const maxAmount = parseFloat(selectedGateway.value.max_amount || Number.MAX_SAFE_INTEGER);
    if (amount > availableBalance.value) return false;
    return amount >= minAmount && amount <= maxAmount;
});

const calculatedCharge = computed(() => {
    if (!selectedGateway.value || !isValidAmount.value) return 0;
    const amount = parseFloat(withdrawalForm.value.amount);
    if (isNaN(amount)) return 0;
    const fixedCharge = parseFloat(selectedGateway.value.fixed_charge || 0);
    const percentCharge = parseFloat(selectedGateway.value.percent_charge || 0);
    return fixedCharge + (amount * percentCharge / 100);
});

const finalAmount = computed(() => {
    if (!isValidAmount.value) return 0;
    const amount = parseFloat(withdrawalForm.value.amount);
    return Math.max(0, amount - calculatedCharge.value);
});

const canSubmit = computed(() => {
    if (!isValidAmount.value || isProcessing.value) return false;
    if (selectedGateway.value?.parameters) {
        const requiredFields = selectedGateway.value.parameters.filter(p => p.field_required);
        for (const field of requiredFields) {
            const value = withdrawalDetails.value[field.field_name];
            if (!value || (typeof value === 'string' && value.trim() === '')) return false;
        }
    }
    return true;
});

const successfulWithdrawals = computed(() => {
    if (!props.withdrawals?.data || !Array.isArray(props.withdrawals.data)) return 0;
    return props.withdrawals.data.filter(w => ['approved', 'completed', 'success'].includes(w.status?.toLowerCase())).length;
});

const pendingWithdrawals = computed(() => {
    if (!props.withdrawals?.data || !Array.isArray(props.withdrawals.data)) return 0;
    return props.withdrawals.data.filter(w => ['pending', 'processing'].includes(w.status?.toLowerCase())).length;
});

const getGatewayCurrencySymbol = (gateway) => {
    if (!gateway?.currency) return '$';
    const currencySymbols = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'BDT': '৳',
        'BTC': '₿', 'ETH': 'Ξ', 'INR': '₹', 'CAD': 'C$', 'AUD': 'A$'
    };
    return currencySymbols[gateway.currency] || gateway.currency + ' ';
};

const getStatusBadgeColor = (status) => {
    if (!status || typeof status !== 'string') return '#94a3b8';
    switch (status.toLowerCase()) {
        case 'approved':
        case 'completed':
        case 'success': return themeColors.value.primary;
        case 'pending':
        case 'processing': return '#fbbf24';
        case 'rejected':
        case 'failed':
        case 'cancelled': return '#ef4444';
        default: return '#94a3b8';
    }
};

const formatNumber = (num) => {
    if (num === null || num === undefined || num === '') return '0.00';
    const number = parseFloat(num);
    if (isNaN(number)) return '0.00';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
    }).format(number);
};

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return t('invalidDate');
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (error) { return t('invalidDate'); }
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch (error) { return ''; }
};

const formatDateTime = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return t('invalidDate');
        return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (error) { return t('invalidDate'); }
};

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown');
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const formatFieldName = (fieldName) => {
    if (!fieldName || typeof fieldName !== 'string') return '';
    return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const selectGateway = (gateway) => {
    if (!gateway?.id) { showToast(t('invalidWithdrawalGateway'), 'error'); return; }
    selectedGateway.value = gateway;
    withdrawalForm.value.withdrawal_gateway_id = gateway.id;
    withdrawalForm.value.amount = null;
    withdrawalDetails.value = {};
};

const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) { showToast(t('fileSizeTooLarge'), 'error'); event.target.value = ''; return; }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) { showToast(t('invalidFileType'), 'error'); event.target.value = ''; return; }
    withdrawalDetails.value[fieldName] = file;
    showToast(t('fileSelectedSuccessfully'), 'success');
};

const submitWithdrawal = async () => {
    if (!canSubmit.value) { showToast(t('ensureRequiredFieldsFilled'), 'error'); return; }
    isProcessing.value = true;
    try {
        const formData = new FormData();
        formData.append('withdrawal_gateway_id', withdrawalForm.value.withdrawal_gateway_id);
        formData.append('amount', withdrawalForm.value.amount);
        formData.append('balance_source', balanceSource.value);
        Object.keys(withdrawalDetails.value).forEach(key => {
            const value = withdrawalDetails.value[key];
            if (value instanceof File) { formData.append(`withdrawal_details[${key}]`, value); }
            else if (value !== null && value !== undefined && value !== '') {
                const sv = String(value).trim();
                if (sv) formData.append(`withdrawal_details[${key}]`, sv);
            }
        });
        router.post('/user/wallet/withdraw', formData, {
            onSuccess: () => { resetForm(); showToast(t('withdrawalRequestSubmitted'), 'success'); },
            onError: (errors) => { const e = Object.values(errors).flat(); showToast(e[0] || t('withdrawalSubmissionFailed'), 'error'); },
            preserveScroll: true,
            forceFormData: true
        });
    } catch (error) { showToast(t('withdrawalSubmissionFailed'), 'error'); }
    finally { isProcessing.value = false; }
};

const showWithdrawalDetails = (withdrawal) => {
    if (!withdrawal?.id) { showToast(t('invalidWithdrawalSelected'), 'error'); return; }
    selectedWithdrawal.value = withdrawal;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    setTimeout(() => { selectedWithdrawal.value = null; }, 300);
};

const showCancelConfirmation = (withdrawal) => {
    if (!withdrawal?.id) { showToast(t('invalidWithdrawalSelected'), 'error'); return; }
    withdrawalToCancel.value = withdrawal;
    showCancelModal.value = true;
};

const closeCancelModal = () => {
    showCancelModal.value = false;
    setTimeout(() => { withdrawalToCancel.value = null; isCancelling.value = false; }, 300);
};

const confirmCancelWithdrawal = async () => {
    if (!withdrawalToCancel.value?.id) return;
    isCancelling.value = true;
    router.patch(`/user/wallet/withdraw/${withdrawalToCancel.value.id}/cancel`, {}, {
        onSuccess: () => { showToast(t('withdrawalCancelledSuccessfully'), 'success'); closeCancelModal(); },
        onError: (errors) => { const e = Object.values(errors).flat(); showToast(e[0] || t('failedToCancelWithdrawal'), 'error'); },
        preserveScroll: true
    });
    isCancelling.value = false;
};

const resetForm = () => {
    selectedGateway.value = null;
    withdrawalForm.value = { amount: null, withdrawal_gateway_id: null };
    withdrawalDetails.value = {};
};

const goToPage = (url) => {
    if (!url || typeof url !== 'string') return;
    router.get(url, {}, { preserveState: true, preserveScroll: true, onError: () => showToast(t('failedToLoadPage'), 'error') });
};

const handleKeydown = (event) => {
    if (event.key === 'Escape') {
        if (showModal.value) closeModal();
        else if (showCancelModal.value) closeCancelModal();
    }
};

onMounted(() => { document.addEventListener('keydown', handleKeydown); });
onUnmounted(() => { document.removeEventListener('keydown', handleKeydown); isProcessing.value = false; isCancelling.value = false; });
</script>

<style scoped>
/* ── Root ── */
.wc { display: flex; flex-direction: column; gap: 24px; }

/* ── Stats ── */
.wc-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.wc-stat-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 16px;
    padding: 18px 16px;
    position: relative;
    overflow: hidden;
}
.wc-stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 15%; right: 15%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), transparent);
    pointer-events: none;
}
.wc-stat-icon {
    width: 38px; height: 38px;
    border-radius: 10px; border: 1px solid;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 12px;
}
.wc-stat-lbl { font-size: 10.5px; font-weight: 700; color: rgba(148,163,184,0.55); text-transform: uppercase; letter-spacing: 0.07em; margin: 0 0 6px; }
.wc-stat-val { font-size: 22px; font-weight: 900; color: #fff; margin: 0 0 6px; line-height: 1; }
.wc-stat-sub { font-size: 11px; font-weight: 700; }

/* ── Main 2-col ── */
.wc-main { display: grid; grid-template-columns: 1fr 340px; gap: 20px; }
.wc-left { display: flex; flex-direction: column; gap: 0; }
.wc-right { display: flex; flex-direction: column; gap: 16px; }

/* ── Card ── */
.wc-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px;
    padding: 24px;
    position: relative;
    overflow: hidden;
}
.wc-card::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.2), transparent);
    pointer-events: none;
}
.wc-card-head { margin-bottom: 20px; }
.wc-card-title { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 5px; }
.wc-card-sub { font-size: 12px; color: rgba(148,163,184,0.55); margin: 0; }
.wc-center { text-align: center; }

/* ── Gateways ── */
.wc-gateways { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
.wc-gateway {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px;
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
}
.wc-gateway:hover:not(.wc-gateway--on) { border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); }
.wc-gateway-icon {
    width: 42px; height: 42px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.wc-gateway-abbr { font-weight: 900; font-size: 13px; color: #0f172a; }
.wc-gateway-info { flex: 1; min-width: 0; }
.wc-gateway-name { font-size: 13px; font-weight: 700; color: #fff; margin: 0 0 4px; }
.wc-gateway-fee { display: inline-block; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 50px; }
.wc-gateway-check { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

/* ── Instructions ── */
.wc-instructions { padding: 14px 16px; border-radius: 12px; border: 1px solid; margin-bottom: 20px; }
.wc-instructions-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 8px; }
.wc-instructions-body { font-size: 12.5px; color: rgba(148,163,184,0.7); line-height: 1.55; }

/* ── Form ── */
.wc-form-wrap { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 20px; }
.wc-form { display: flex; flex-direction: column; gap: 16px; }
.wc-field { display: flex; flex-direction: column; gap: 7px; }
.wc-field-lbl { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }
.wc-field-hint { font-size: 11px; color: rgba(148,163,184,0.45); margin: 0; }
.wc-required { color: #f87171; margin-left: 3px; }
.wc-input {
    width: 100%; padding: 11px 14px; border-radius: 11px;
    border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04);
    color: #fff; font-size: 13.5px; font-weight: 500; outline: none;
    transition: border-color 0.18s; box-sizing: border-box;
}
.wc-input:focus { border-color: rgba(167,93,237,0.45); }
.wc-input::placeholder { color: rgba(148,163,184,0.35); }
.wc-select { cursor: pointer; }
.wc-select option { background: #0a0416; color: #fff; }
.wc-file-input { width: 100%; font-size: 12.5px; color: rgba(148,163,184,0.7); }
.wc-params { display: flex; flex-direction: column; gap: 14px; }
.wc-params-title { font-size: 13px; font-weight: 800; color: #fff; margin: 0; }
.wc-submit {
    width: 100%; padding: 13px 20px; border-radius: 12px; border: none;
    font-size: 14px; font-weight: 800; cursor: pointer;
    transition: all 0.2s ease; letter-spacing: 0.02em;
    display: flex; align-items: center; justify-content: center; gap: 8px;
}
.wc-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.08); }
.wc-submit:disabled { opacity: 0.5; cursor: not-allowed; }
.wc-spin { animation: wcSpin 1s linear infinite; flex-shrink: 0; }
@keyframes wcSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* ── Summary ── */
.wc-summary { }
.wc-summary-rows { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px; }
.wc-sum-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.wc-sum-lbl { font-size: 12.5px; color: rgba(148,163,184,0.6); font-weight: 600; }
.wc-sum-val { font-size: 13px; font-weight: 700; color: #fff; }
.wc-sum-total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
.wc-sum-lbl--b { font-size: 13px; font-weight: 800; color: #fff; }
.wc-sum-total-val { font-size: 20px; font-weight: 900; }
.wc-deduct { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-radius: 10px; border: 1px solid; }
.wc-deduct-lbl { font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 600; }
.wc-deduct-val { font-size: 17px; font-weight: 900; }

/* ── Gateway logo ── */
.wc-gateway-logo { display: flex; justify-content: center; margin-bottom: 14px; }
.wc-gateway-logo-img { width: 72px; height: 72px; object-fit: contain; border-radius: 14px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); padding: 10px; }
.wc-details-list { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
.wc-detail-row { display: flex; justify-content: space-between; align-items: center; }
.wc-detail-lbl { font-size: 12px; color: rgba(148,163,184,0.55); font-weight: 600; }
.wc-detail-val { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }

/* ── Tips ── */
.wc-tips { display: flex; flex-direction: column; gap: 12px; margin-top: 14px; }
.wc-tip { display: flex; align-items: flex-start; gap: 10px; }
.wc-tip-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex-shrink: 0; }
.wc-tip-title { font-size: 12.5px; font-weight: 700; color: #fff; margin: 0 0 2px; }
.wc-tip-sub { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; line-height: 1.4; }

/* ── History ── */
.wc-hist-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
}
.wc-hist-card::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), rgba(232,121,249,0.2), transparent);
    pointer-events: none;
}
.wc-hist-head {
    display: flex; align-items: center; gap: 14px;
    padding: 20px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-wrap: wrap;
}
.wc-hist-head-icon {
    width: 36px; height: 36px; border-radius: 10px; border: 1px solid;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.wc-hist-title { font-size: 14.5px; font-weight: 800; color: #fff; margin: 0 0 3px; }
.wc-hist-sub { font-size: 11.5px; color: rgba(148,163,184,0.55); margin: 0; }
.wc-hist-chip {
    margin-left: auto;
    display: flex; align-items: center; gap: 6px;
    padding: 6px 13px; border-radius: 50px; border: 1px solid;
    font-size: 11px; font-weight: 700; color: rgba(148,163,184,0.55);
}
.wc-hist-chip span:first-child { font-size: 14px; font-weight: 900; }

/* ── Empty ── */
.wc-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 56px 24px; text-align: center; }
.wc-empty-ic { width: 64px; height: 64px; border-radius: 18px; border: 1px solid; display: flex; align-items: center; justify-content: center; }
.wc-empty-title { font-size: 15px; font-weight: 700; color: #fff; margin: 4px 0 0; }
.wc-empty-sub { font-size: 12.5px; color: rgba(148,163,184,0.5); margin: 0; }

/* ── Mobile rows ── */
.wc-mob { display: flex; flex-direction: column; }
@media (min-width: 1024px) { .wc-mob { display: none !important; } }
.wc-row {
    display: grid; grid-template-columns: 1fr auto; grid-template-rows: auto auto;
    gap: 6px 12px; padding: 14px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    border-left: 3px solid transparent;
    transition: all 0.18s;
    align-items: center;
}
.wc-row:hover { background: rgba(167,93,237,0.04); border-left-color: rgba(167,93,237,0.5); }
.wc-row:last-child { border-bottom: none; }
.wc-row-l { display: flex; align-items: center; gap: 10px; min-width: 0; grid-column: 1; grid-row: 1; }
.wc-row-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.wc-row-trx { font-size: 12.5px; font-weight: 700; color: #fff; margin: 0 0 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wc-row-date { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; }
.wc-row-r { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; grid-column: 2; grid-row: 1; }
.wc-row-amount { font-size: 13px; font-weight: 800; margin: 0; }
.wc-row-badge { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 50px; border: 1px solid; }
.wc-row-actions { grid-column: 1 / -1; grid-row: 2; display: flex; gap: 8px; }
.wc-cancel-btn { font-size: 11.5px; font-weight: 700; padding: 5px 12px; border-radius: 8px; border: 1px solid rgba(239,68,68,0.3); background: rgba(239,68,68,0.08); color: #f87171; cursor: pointer; transition: all 0.18s; }
.wc-view-btn { flex: 1; font-size: 11.5px; font-weight: 700; padding: 6px 14px; border-radius: 8px; border: 1px solid; background: transparent; cursor: pointer; transition: all 0.18s; }

/* ── Desktop table ── */
.wc-tbl-wrap { display: none; overflow-x: auto; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
@media (min-width: 1024px) { .wc-tbl-wrap { display: block; } }
.wc-tbl { width: 100%; border-collapse: collapse; min-width: 900px; }
.wc-tbl-hdr { border-bottom: 1px solid rgba(255,255,255,0.06); }
.wc-th { padding: 11px 16px; text-align: left; font-size: 10.5px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: rgba(148,163,184,0.5); white-space: nowrap; }
.wc-tr { border-bottom: 1px solid rgba(255,255,255,0.04); transition: background 0.15s; }
.wc-tr:last-child { border-bottom: none; }
.wc-tr:hover { background: rgba(167,93,237,0.04); }
.wc-td { padding: 13px 16px; vertical-align: middle; }
.wc-mono { font-family: monospace; font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.9); }
.wc-td-val { font-size: 13px; font-weight: 700; color: rgba(226,232,240,0.9); display: block; }
.wc-td-fee { font-size: 11px; font-weight: 600; color: #fbbf24; display: block; }
.wc-td-muted { font-size: 12.5px; color: rgba(148,163,184,0.65); }
.wc-td-date { font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.75); margin: 0 0 2px; }
.wc-td-time { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; }
.wc-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 50px; border: 1px solid; white-space: nowrap; }
.wc-badge-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; flex-shrink: 0; animation: wcBlink 2s ease-in-out infinite; }
.wc-badge-dot--pulse { animation: wcBlink 2s ease-in-out infinite; }
@keyframes wcBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.wc-tbl-cancel { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 7px; border: 1px solid rgba(239,68,68,0.3); background: rgba(239,68,68,0.08); color: #f87171; cursor: pointer; transition: all 0.18s; }
.wc-tbl-view { width: 30px; height: 30px; border-radius: 8px; border: 1px solid; display: inline-flex; align-items: center; justify-content: center; background: transparent; cursor: pointer; transition: all 0.18s; }
.wc-tbl-view:hover { transform: scale(1.1); filter: brightness(1.2); }

/* ── Pagination ── */
.wc-pagi { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.06); }
.wc-pagi-info { font-size: 11.5px; color: rgba(148,163,184,0.55); font-weight: 500; }
.wc-pagi-btns { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.wc-pagi-btn { min-width: 32px; height: 32px; padding: 0 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.7); font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.18s; display: inline-flex; align-items: center; justify-content: center; }
.wc-pagi-btn:hover:not(.wc-pagi-btn--on):not(.wc-pagi-btn--off) { background: rgba(255,255,255,0.08); color: #fff; border-color: rgba(255,255,255,0.2); }
.wc-pagi-btn--on { border-color: transparent; font-weight: 800; color: #0f172a; }
.wc-pagi-btn--off { opacity: 0.3; cursor: not-allowed; }

/* ── Modals ── */
.wc-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.72); backdrop-filter: blur(6px); z-index: 50; display: flex; align-items: center; justify-content: center; padding: 16px; }
.wc-modal {
    background: rgba(10,4,22,0.98);
    border: 1px solid rgba(167,93,237,0.2);
    border-radius: 22px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6);
    width: 100%; max-width: 680px;
    max-height: 90vh; overflow-y: auto;
    position: relative;
}
.wc-modal::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.5), rgba(232,121,249,0.3), transparent);
    pointer-events: none;
}
.wc-modal--sm { max-width: 460px; }
.wc-modal-head { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); position: sticky; top: 0; background: rgba(10,4,22,0.98); z-index: 2; }
.wc-modal-title { font-size: 17px; font-weight: 900; color: #fff; margin: 0; }
.wc-modal-close { width: 32px; height: 32px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04); color: rgba(148,163,184,0.7); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.18s; }
.wc-modal-close:hover { background: rgba(255,255,255,0.08); color: #fff; }
.wc-modal-body { padding: 20px 24px; display: flex; flex-direction: column; gap: 16px; }
.wc-modal-foot { display: flex; align-items: center; padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(5,2,15,0.5); position: sticky; bottom: 0; }
.wc-modal-hcard { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 16px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; }
.wc-modal-hcard-l { display: flex; align-items: center; gap: 12px; min-width: 0; }
.wc-modal-hcard-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.wc-modal-plan { font-size: 15px; font-weight: 800; color: #fff; margin: 0 0 3px; }
.wc-modal-plan-sub { font-size: 11px; color: rgba(148,163,184,0.5); margin: 0; }
.wc-modal-trx { font-family: monospace; font-size: 12.5px; font-weight: 700; padding: 10px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); }
.wc-modal-section { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
.wc-modal-section-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; margin: 0; }
.wc-modal-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.wc-modal-field { display: flex; flex-direction: column; gap: 4px; }
.wc-modal-field-lbl { font-size: 10.5px; color: rgba(148,163,184,0.5); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
.wc-modal-field-val { font-size: 13px; font-weight: 700; color: #fff; }
.wc-modal-amounts { display: flex; flex-direction: column; gap: 10px; }
.wc-modal-amount-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.wc-modal-amount-lbl { font-size: 12.5px; color: rgba(148,163,184,0.6); font-weight: 600; }
.wc-modal-amount-val { font-size: 13.5px; font-weight: 700; color: rgba(226,232,240,0.9); }
.wc-modal-amount-total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); }
.wc-modal-amount-lbl--b { font-size: 13.5px; font-weight: 800; color: #fff; }
.wc-modal-total-val { font-size: 22px; font-weight: 900; }
.wc-modal-note { font-size: 13px; color: rgba(148,163,184,0.7); line-height: 1.55; margin: 0; }
.wc-close-btn { padding: 10px 20px; border-radius: 11px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.05); color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.18s; }
.wc-close-btn:hover { background: rgba(255,255,255,0.1); }

/* ── Cancel modal ── */
.wc-cancel-icon-wrap { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; padding: 8px 0; }
.wc-cancel-icon { width: 64px; height: 64px; border-radius: 50%; background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.25); display: flex; align-items: center; justify-content: center; color: #f87171; }
.wc-cancel-heading { font-size: 15px; font-weight: 800; color: #fff; margin: 0; }
.wc-cancel-desc { font-size: 12.5px; color: rgba(148,163,184,0.6); margin: 0; }
.wc-cancel-info { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 14px; display: flex; flex-direction: column; gap: 8px; }
.wc-refund-notice { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-radius: 11px; border: 1px solid; }
.wc-refund-text { font-size: 13px; color: rgba(226,232,240,0.8); margin: 0; }
.wc-cancel-confirm-btn { padding: 10px 20px; border-radius: 11px; border: none; background: #dc2626; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.18s; display: flex; align-items: center; gap: 7px; }
.wc-cancel-confirm-btn:hover:not(:disabled) { background: #b91c1c; }
.wc-cancel-confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Responsive ── */
@media (max-width: 1200px) { .wc-main { grid-template-columns: 1fr 300px; } }
@media (max-width: 1024px) { .wc-main { grid-template-columns: 1fr; } .wc-right { display: none; } .wc-stats { grid-template-columns: 1fr 1fr; } }
@media (max-width: 640px) { .wc-stats { grid-template-columns: 1fr 1fr; gap: 10px; } .wc-gateways { grid-template-columns: 1fr; } .wc-modal-grid2 { grid-template-columns: 1fr; } .wc-hist-chip { display: none; } .wc-hist-head { padding: 14px 16px 12px; } .wc-row { padding: 12px 14px; } .wc-pagi { flex-direction: column; align-items: flex-start; gap: 8px; } }
@media (max-width: 480px) { .wc-stats { grid-template-columns: 1fr 1fr; } .wc-stat-val { font-size: 18px; } }

button:focus-visible { outline: 2px solid rgba(255,255,255,0.25); outline-offset: 2px; }

</style>
